class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    // ── KEY FIX ──────────────────────────────────────────────────────────────
    // Shopify's sticky-header applies transform:translateY() which breaks
    // position:fixed on any child element (CSS spec: fixed is relative to the
    // nearest transformed ancestor, not the viewport). Moving the notification
    // directly onto <body> guarantees no transformed ancestor exists.
    document.body.appendChild(this.notification);
    // Ensure notification is always hidden on page load — guards against
    // bfcache restoring a previously-open state.
    this.notification.classList.remove('active', 'animate');
    // ─────────────────────────────────────────────────────────────────────────

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    /* Wire close buttons — search inside notification (now on body) not inside this element */
    this.notification.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    /* Use delegation on stable notification container — survives renderContents() innerHTML replacements */
    this.notification.addEventListener('click', (e) => {
      if (e.target.closest('button[name="checkout"], #cart-notification-form')) {
        e.preventDefault();
        window.location.href = '/checkout';
      }
    });

    // Close instantly on tab switch — skip animation so there is no
    // mid-transition state that could flash when the tab becomes visible again.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.closeInstant();
    });

    // Close on page navigation — handles back/forward (popstate) and
    // bfcache restore (pageshow persisted) so notification never bleeds
    // into a different page after navigate or refresh.
    window.addEventListener('popstate', () => this.closeInstant());
    window.addEventListener('pageshow', () => this.closeInstant());
  }

  open() {
    this.notification.removeAttribute('hidden');
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    setTimeout(() => document.body.addEventListener('click', this.onBodyClick), 0);

    /* Auto-close after 6 seconds */
    clearTimeout(this._autoCloseTimer);
    this._autoCloseTimer = setTimeout(() => this.close(), 6000);
  }

  // Normal close — smooth fade (X button / body click / Escape).
  close() {
    if (!this.notification.classList.contains('active')) return;

    clearTimeout(this._autoCloseTimer);
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);

    // Clean up animate class after the fade finishes.
    this.notification.addEventListener(
      'transitionend',
      () => this.notification.classList.remove('animate'),
      { once: true }
    );
  }

  // Instant close — no animation (tab switch / navigation).
  closeInstant() {
    clearTimeout(this._autoCloseTimer);
    this.notification.classList.remove('active', 'animate');
    this.notification.setAttribute('hidden', '');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
